import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import { useReactToPrint } from "react-to-print";
import { saveAs } from "file-saver";
import { useApp } from "../../AppProvider";
import SnapshotPrintContent from "./SnapshotPrintContent";
import { buildSnapshotDocx } from "./buildSnapshotDocx";
import { blankSnapshotData, reportFileName } from "./snapshotFormat";

const API_BASE = `${process.env.REACT_APP_ENDPOINT}/api/field-services`;
const LOGO_PATH = "/static/img/pdf-print-logo.png";

// Load a fetched image and record its natural dimensions (needed to size
// ImageRuns in the .docx and to keep object URLs for the print layout).
const measureImage = (arrayBuffer) =>
  new Promise((resolve) => {
    const blob = new Blob([arrayBuffer]);
    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () =>
      resolve({
        arrayBuffer,
        objectUrl,
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };
    img.src = objectUrl;
  });

/**
 * Generates the Field Services Snapshot Report for an inspection, either as
 * a printable PDF (react-to-print) or a downloaded .docx.
 *
 * const { generateReport, generating, printMarkup } = useSnapshotReport();
 * generateReport(irNdx, "pdf" | "docx");
 * `printMarkup` must be rendered somewhere in the tree (it's hidden).
 */
export const useSnapshotReport = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { doToast } = useApp();

  // {irNdx, format} while a report is being generated, else null
  const [generating, setGenerating] = useState(null);
  const [printPayload, setPrintPayload] = useState(null);

  const printRef = useRef(null);
  const objectUrlsRef = useRef([]);

  const releaseObjectUrls = useCallback(() => {
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    objectUrlsRef.current = [];
  }, []);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onAfterPrint: () => {
      setPrintPayload(null);
      setGenerating(null);
      releaseObjectUrls();
    },
  });

  useEffect(() => {
    if (printPayload) handlePrint();
  }, [printPayload]); //eslint-disable-line

  useEffect(() => releaseObjectUrls, [releaseObjectUrls]);

  const fetchSnapshot = useCallback(
    async (irNdx) => {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };

      const { data } = await axios.get(
        `${API_BASE}/inspections/${irNdx}/snapshot`,
        { headers }
      );

      const photos = (
        await Promise.all(
          data.photos.map(async (photo) => {
            try {
              const response = await axios.get(
                `${API_BASE}/inspections/attachments/${photo.att_ndx}/file`,
                { headers, responseType: "arraybuffer" }
              );
              const measured = await measureImage(response.data);
              return measured ? { ...photo, ...measured } : null;
            } catch (err) {
              console.error(`Failed to load photo ${photo.att_ndx}`, err);
              return null;
            }
          })
        )
      ).filter(Boolean);

      photos.forEach((photo) => objectUrlsRef.current.push(photo.objectUrl));

      return { data, photos };
    },
    [getAccessTokenSilently]
  );

  const fetchLogo = useCallback(async () => {
    try {
      const response = await axios.get(LOGO_PATH, {
        responseType: "arraybuffer",
      });
      const measured = await measureImage(response.data);
      if (measured) objectUrlsRef.current.push(measured.objectUrl);
      return measured;
    } catch (err) {
      console.error("Failed to load report logo", err);
      return null;
    }
  }, []);

  const generateReport = useCallback(
    async (irNdx, format) => {
      if (generating) return;
      setGenerating({ irNdx, format });
      try {
        const { data, photos } = await fetchSnapshot(irNdx);

        if (format === "docx") {
          const logo = await fetchLogo();
          const blob = await buildSnapshotDocx({ data, photos, logo });
          saveAs(blob, reportFileName(data.inspection, "docx"));
          setGenerating(null);
          releaseObjectUrls();
        } else {
          // PDF: render the hidden print layout, then let the effect fire
          // the browser print dialog once it's mounted.
          setPrintPayload({ data, photos });
        }
      } catch (err) {
        console.error("Failed to generate inspection report", err);
        doToast(
          "error",
          "Unable to generate the Snapshot Report. Please try again."
        );
        setGenerating(null);
        releaseObjectUrls();
      }
    },
    [generating, fetchSnapshot, fetchLogo, releaseObjectUrls, doToast]
  );

  // Empty field sheet for taking on site visits — no API calls involved.
  const generateBlank = useCallback(
    async (format) => {
      if (generating) return;
      setGenerating({ irNdx: "blank", format });
      try {
        const data = blankSnapshotData();
        if (format === "docx") {
          const logo = await fetchLogo();
          const blob = await buildSnapshotDocx({
            data,
            photos: [],
            logo,
            blank: true,
          });
          saveAs(blob, "inspection-field-sheet_blank-template.docx");
          setGenerating(null);
          releaseObjectUrls();
        } else {
          setPrintPayload({ data, photos: [], blank: true });
        }
      } catch (err) {
        console.error("Failed to generate blank field sheet", err);
        doToast(
          "error",
          "Unable to generate the blank field sheet. Please try again."
        );
        setGenerating(null);
        releaseObjectUrls();
      }
    },
    [generating, fetchLogo, releaseObjectUrls, doToast]
  );

  const printMarkup = useMemo(() => {
    if (!printPayload) return null;
    return (
      <SnapshotPrintContent
        data={printPayload.data}
        photos={printPayload.photos}
        blank={printPayload.blank}
        ref={printRef}
      />
    );
  }, [printPayload]);

  return { generateReport, generateBlank, generating, printMarkup };
};
